import React, { useState, useEffect, useMemo } from 'react';
import { X, Plus, Trash2, CornerDownRight, Share2 } from 'lucide-react';
import { useWorkspaceNotesV2, useEntityTypes, useWorkspaceNoteTags } from '../../hooks/useNotesV2';
import { NoteV2 } from '../../services/notesV2Api';
import Modal from '@shared/ui/Modal';
import RichTextEditor from '../RichTextEditor';
import EntitySelector from './EntitySelector';
import Button from '../Button';
import MultiSelectDropdown from '../MultiSelectDropdown';
import { addTagToList, filterTagSuggestions } from '../../utils/noteTagUtils';

// Helper to strip HTML and get plain text preview
const getContentPreview = (html: string, maxLength: number = 150): string => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  const text = tempDiv.textContent || tempDiv.innerText || '';
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
};

interface EntityData {
  entity_type: string;
  entity_id: string;
  entity_display_name?: string;
}

interface NoteFormData {
  title?: string;
  content: string;
  include_in_reports: boolean;
  tags: string[];
  entities: EntityData[];
}

interface NoteFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * The workspace the note belongs to. Omit it and pass `workspaceOptions`
   * instead to run the modal outside any workspace: a required multi-select
   * chooses the destination workspace(s) and `onSaveMulti` receives them.
   */
  workspaceId?: string;
  /** Write-capable workspaces for the cross-workspace create mode. */
  workspaceOptions?: Array<{ id: string; name: string }>;
  /** Create callback for the cross-workspace mode. */
  onSaveMulti?: (workspaceIds: string[], noteData: NoteFormData) => void;
  // If note is provided, we're in edit mode
  note?: NoteV2;
  // For create mode - callback when note is created
  onSave?: (noteData: NoteFormData) => void;
  // For create mode - callback when note is created and should open share modal
  onCreateAndShare?: (noteData: NoteFormData) => void;
  // For create mode - loading state
  isSaving?: boolean;
  // Default entities to associate with new notes
  defaultEntities?: EntityData[];
  // For reply mode - the parent note being replied to
  replyToNote?: NoteV2;
  // For reply mode - callback when reply is submitted
  onReply?: (parentNoteId: string, content: string, title: string, includeInReports: boolean) => void;
  // For reply mode - loading state
  isReplying?: boolean;
}

const NoteFormModal: React.FC<NoteFormModalProps> = ({
  isOpen,
  onClose,
  workspaceId,
  workspaceOptions,
  onSaveMulti,
  note,
  onSave,
  onCreateAndShare,
  isSaving = false,
  defaultEntities = [],
  replyToNote,
  onReply,
  isReplying = false
}) => {
  const isEditMode = !!note;
  const isReplyMode = !!replyToNote;
  const isCreateMode = !isEditMode && !isReplyMode;
  const isMultiWorkspace = isCreateMode && !workspaceId && !!workspaceOptions;

  // Cross-workspace mode: the chosen destination(s). Preselected when there
  // is only one option — no reason to make the user say the obvious.
  const [selectedWorkspaceIds, setSelectedWorkspaceIds] = useState<string[]>(
    workspaceOptions?.length === 1 ? [workspaceOptions[0].id] : []
  );

  /*
    Workspace used for tag suggestions and workspace-scoped entity search.
    In multi mode it exists only while exactly one workspace is selected;
    both consumers degrade gracefully without one (no suggestions, global
    entity search).
  */
  const effectiveWorkspaceId =
    workspaceId ?? (selectedWorkspaceIds.length === 1 ? selectedWorkspaceIds[0] : '');

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [includeInReports, setIncludeInReports] = useState(false); // Default to false
  const [tags, setTags] = useState<string[]>([]);
  const [currentTag, setCurrentTag] = useState('');

  // Tags already in use in this workspace, so they can be reused instead of
  // remembered and retyped.
  const { tags: workspaceTags } = useWorkspaceNoteTags(effectiveWorkspaceId);

  /** Vocabulary matching what's typed, minus what this note already carries. */
  const tagSuggestions = useMemo(
    () => filterTagSuggestions(workspaceTags, tags, currentTag),
    [workspaceTags, tags, currentTag]
  );
  const [entities, setEntities] = useState<EntityData[]>(defaultEntities);
  const [newEntity, setNewEntity] = useState<EntityData>({
    entity_type: '',
    entity_id: '',
    entity_display_name: ''
  });
  // Track if user clicked "Create & Share" to call the right callback
  const [pendingShareAction, setPendingShareAction] = useState(false);

  // For edit mode, use the update hook (edit always has a workspaceId)
  const { updateNote, isUpdating } = useWorkspaceNotesV2(workspaceId ?? '');
  const { entityTypes, isLoading: loadingEntityTypes } = useEntityTypes();
  
  const availableEntityTypes = entityTypes.filter(entityType =>
    entityType.name === 'bill' || entityType.name === 'legislator' || entityType.name === 'calendar' || entityType.name === 'budget_item' || entityType.name === 'ballot_initiative'
  );

  // Track if we've initialized to prevent constant resets
  const isOpenRef = React.useRef(false);
  
  // Reset/populate form when modal opens
  useEffect(() => {
    if (isOpen && !isOpenRef.current) {
      isOpenRef.current = true;
      
      if (isEditMode && note) {
        // Edit mode - populate from note
        setTitle(note.title || '');
        setContent(note.content || '');
        setIncludeInReports(note.include_in_reports);
        setTags(note.tags || []);
        setEntities(note.entities.map(entity => ({
          entity_type: entity.entity_type,
          entity_id: entity.entity_id,
          entity_display_name: entity.entity_display_name
        })));
      } else if (isReplyMode && replyToNote) {
        // Reply mode - auto-populate title as "Re: {parent title}"
        const parentTitle = replyToNote.title || 'Untitled';
        setTitle(`Re: ${parentTitle}`);
        setContent('');
        setIncludeInReports(false); // Default to false for replies
        setTags([]); // Replies don't need tags
        setEntities([]); // Entities are inherited from parent via backend
      } else {
        // Create mode - reset to defaults
        setTitle('');
        setContent('');
        setIncludeInReports(false); // Default to false
        setTags([]);
        setSelectedWorkspaceIds(
          workspaceOptions?.length === 1 ? [workspaceOptions[0].id] : []
        );
        // Filter out any default entities with empty/missing IDs
        const validDefaultEntities = defaultEntities.filter(e => e.entity_id && e.entity_id.trim() !== '');
        setEntities(validDefaultEntities);
      }
      setCurrentTag('');
      setNewEntity({ entity_type: '', entity_id: '', entity_display_name: '' });
    } else if (!isOpen && isOpenRef.current) {
      isOpenRef.current = false;
    }
  }, [isOpen, note, isEditMode, defaultEntities, isReplyMode, replyToNote, workspaceOptions]);

  const [entityError, setEntityError] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setEntityError(null);
    
    // Validate content
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    
    if (textContent.trim().length === 0) {
      return;
    }

    // Entity association is optional - notes can exist without entities
    // But if entities ARE provided (and not in reply mode), validate they have valid IDs
    if (!isReplyMode && entities.length > 0) {
      const invalidEntities = entities.filter(e => !e.entity_id || e.entity_id.trim() === '');
      if (invalidEntities.length > 0) {
        setEntityError('One or more entities have invalid IDs. Please remove them and add valid entities.');
        return;
      }
    }

    // Auto-add tag if user typed something but forgot to click "Add"
    let finalTags = [...tags];
    if (finalTags.length === 0 && currentTag.trim()) {
      finalTags = [currentTag.trim()];
    }

    if (isReplyMode && replyToNote && onReply) {
      // Reply mode - use onReply callback
      onReply(replyToNote.id, content, title.trim(), includeInReports);
    } else if (isEditMode && note) {
      // Edit mode - use updateNote hook
      const noteData: NoteFormData = {
        title: title.trim() || undefined,
        content,
        include_in_reports: includeInReports,
        tags: finalTags,
        entities
      };
      updateNote(
        { noteId: note.id, noteData },
        { onSuccess: () => onClose() }
      );
    } else {
      // Create mode - use callback
      const noteData: NoteFormData = {
        title: title.trim() || undefined,
        content,
        include_in_reports: includeInReports,
        tags: finalTags,
        entities
      };
      
      // Check if user clicked "Create & Share"
      if (pendingShareAction && onCreateAndShare) {
        onCreateAndShare(noteData);
        setPendingShareAction(false);
      } else if (isMultiWorkspace && onSaveMulti) {
        if (selectedWorkspaceIds.length === 0) return; // button is disabled anyway
        onSaveMulti(selectedWorkspaceIds, noteData);
      } else if (onSave) {
        onSave(noteData);
      }
    }
  };

  const addTag = () => {
    setTags(addTagToList(tags, currentTag, workspaceTags));
    setCurrentTag('');
  };

  const addSuggestedTag = (label: string) => {
    setTags(addTagToList(tags, label, workspaceTags));
    setCurrentTag('');
  };

  const removeTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const addEntity = () => {
    // Validate entity has both type and a non-empty ID
    if (newEntity.entity_type && newEntity.entity_id && newEntity.entity_id.trim() !== '') {
      const entity = {
        ...newEntity,
        entity_display_name: newEntity.entity_display_name || 
          `${newEntity.entity_type}: ${newEntity.entity_id}`
      };
      
      // Check if entity already exists
      const exists = entities.some(e => 
        e.entity_type === entity.entity_type && e.entity_id === entity.entity_id
      );
      
      if (!exists) {
        setEntities([...entities, entity]);
        setNewEntity({ entity_type: '', entity_id: '', entity_display_name: '' });
      }
    }
  };

  const handleEntitySelect = (entityId: string, displayName: string) => {
    setNewEntity({
      ...newEntity,
      entity_id: entityId,
      entity_display_name: displayName
    });
  };

  const removeEntity = (index: number) => {
    setEntities(entities.filter((_, i) => i !== index));
  };

  const handleKeyPress = (e: React.KeyboardEvent, action: () => void) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      action();
    }
  };

  const isLoading = isEditMode ? isUpdating : (isReplyMode ? isReplying : isSaving);

  const heading = isReplyMode ? 'Reply to note' : isEditMode ? 'Edit note' : 'Create note';
  const subheading = isEditMode
    ? 'Update your note content and settings'
    : isReplyMode
      ? 'Add your reply to the note below'
      : undefined;

  /*
    <Modal> directly rather than <FormModal>: this dialog has TWO submits with
    different variants ("Create & Share" beside "Create note"), which FormModal's
    single-submit footer cannot express. The <form id> + `form=` association is
    the same mechanism FormModal uses, so the footer still stays pinned while the
    fields scroll and Enter still submits.
  */
  return (
    <Modal
      isOpen={isOpen}
      title={heading}
      description={subheading}
      onClose={onClose}
      size="xl"
      /* Unsaved note content must survive a stray backdrop click. */
      dismissLocked={content.trim().length > 0 && !isLoading}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          {isCreateMode && onCreateAndShare && (
            <Button
              type="submit"
              form="note-form"
              variant="secondary"
              disabled={isLoading || !content.trim()}
              loading={isLoading && pendingShareAction}
              onClick={() => setPendingShareAction(true)}
              icon={Share2}
            >
              Create &amp; share
            </Button>
          )}
          <Button
            type="submit"
            form="note-form"
            variant="primary"
            disabled={
              isLoading ||
              !content.trim() ||
              (isMultiWorkspace && selectedWorkspaceIds.length === 0)
            }
            loading={isLoading && !pendingShareAction}
            onClick={() => setPendingShareAction(false)}
          >
            {isReplyMode ? 'Send reply' : isEditMode ? 'Update note' : 'Create note'}
          </Button>
        </>
      }
    >
      <form id="note-form" onSubmit={handleSubmit} className="space-y-6">
            {/* Parent Note Preview - shown in reply mode */}
            {isReplyMode && replyToNote && (
              <div className="p-4 bg-white dark:bg-background-dark-secondary rounded-lg border-l-4 border-primary-500">
                <div className="flex items-center gap-2 text-xs text-text-tertiary dark:text-text-dark-tertiary mb-2">
                  <CornerDownRight className="w-3 h-3" />
                  <span>Replying to note by {replyToNote.author?.display_name || 'Unknown'}</span>
                </div>
                <div className="text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">
                  {replyToNote.title || 'Untitled'}
                </div>
                <div className="text-sm text-text-secondary dark:text-text-dark-secondary line-clamp-3">
                  {getContentPreview(replyToNote.content, 200)}
                </div>
              </div>
            )}

            {/* Workspace destination(s) — only in the cross-workspace mode */}
            {isMultiWorkspace && (
              <div>
                <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-2 text-left">
                  Workspaces *
                </label>
                <MultiSelectDropdown
                  options={(workspaceOptions ?? []).map((w) => ({ value: w.id, label: w.name }))}
                  selected={selectedWorkspaceIds}
                  onChange={setSelectedWorkspaceIds}
                  allLabel="All workspaces"
                  selectedWord="selected"
                  ariaLabel="Workspaces to save the note in"
                  searchable={(workspaceOptions ?? []).length > 10}
                  searchPlaceholder="Search workspaces…"
                />
                {selectedWorkspaceIds.length === 0 && (
                  <p className="mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary text-left">
                    Choose at least one workspace for this note.
                  </p>
                )}
              </div>
            )}

            {/* Title and Include in Reports - Same Row */}
            <div className="flex flex-col md:flex-row md:items-end gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-2 text-left">
                  Title (optional)
                </label>
                <input
                  id="note-title"
                  name="title"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="input"
                  placeholder="Enter note title..."
                  autoComplete="off"
                />
              </div>
              <div className="flex items-center pb-2 shrink-0">
                <input
                  type="checkbox"
                  id="includeInReports"
                  checked={includeInReports}
                  onChange={(e) => setIncludeInReports(e.target.checked)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-border-light dark:border-border-dark rounded"
                />
                <label htmlFor="includeInReports" className="ml-2 text-sm text-text-primary dark:text-text-dark-primary whitespace-nowrap">
                  Include in reports
                </label>
              </div>
            </div>

            {/* Content */}
            <div>
              <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-2 text-left">
                Content *
              </label>
              <div className="min-h-[200px] max-h-[300px] overflow-hidden">
                <RichTextEditor
                  value={content}
                  onChange={setContent}
                  placeholder="Write your note content..."
                />
              </div>
            </div>

            {/* Associated Entities - Hidden in reply mode (entities are inherited from parent) */}
            {!isReplyMode && (
              <div>
                <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-2 text-left">
                  Associated Entities * {entities.length > 0 && `(${entities.length})`}
                </label>
                
                {/* Entity validation error */}
                {entityError && (
                  <div className="mb-3 p-3 bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800 rounded-lg">
                    <p className="text-sm text-error-700 dark:text-error-300">{entityError}</p>
                  </div>
                )}
                
                {/* Current Entities */}
                {entities.length > 0 && (
                  <div className="mb-3 space-y-2">
                    {entities.map((entity, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-2 bg-primary-50 dark:bg-primary-900/30 rounded-md"
                      >
                        <div className="flex-1 text-left">
                          <span className="text-sm font-medium text-primary-900 dark:text-primary-200">
                            {entity.entity_type.charAt(0).toUpperCase() + entity.entity_type.slice(1)}:
                          </span>
                          <span className="text-sm text-primary-700 dark:text-primary-300 ml-1">
                            {entity.entity_display_name || entity.entity_id}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeEntity(index)}
                          className="text-error-500 hover:text-error-700 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add New Entity */}
                <div className="flex flex-col md:flex-row gap-2">
                  <select
                    value={newEntity.entity_type}
                    onChange={(e) => setNewEntity({ entity_type: e.target.value, entity_id: '', entity_display_name: '' })}
                    className="input w-full md:w-40 shrink-0"
                    disabled={loadingEntityTypes}
                  >
                    <option value="">Select type...</option>
                    {availableEntityTypes.map((type) => (
                      <option key={type.id} value={type.name}>
                        {type.display_name}
                      </option>
                    ))}
                  </select>

                  <div className="flex-1 min-w-0">
                    <EntitySelector
                      entityType={newEntity.entity_type}
                      value={newEntity.entity_id}
                      onChange={handleEntitySelect}
                      placeholder={newEntity.entity_type ? `Select ${newEntity.entity_type}...` : "Select entity type first"}
                      disabled={!newEntity.entity_type}
                      workspaceId={effectiveWorkspaceId || undefined}
                    />
                  </div>

                  <Button
                    variant="primary"
                    size="sm"
                    onClick={addEntity}
                    disabled={!newEntity.entity_type || !newEntity.entity_id}
                    icon={Plus}
                    ariaLabel="Add entity"
                    className="shrink-0 w-10 md:w-10"
                  >
                    {/* Icon only */}
                  </Button>
                </div>
              </div>
            )}

            {/* Tags - Hidden in reply mode */}
            {!isReplyMode && (
              <div>
                <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-2 text-left">
                  Tags
                </label>
                
                {/* Current Tags */}
                {tags.length > 0 && (
                  <div className="mb-3 flex flex-wrap gap-2">
                    {tags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 py-1 bg-background-tertiary dark:bg-background-dark-tertiary text-text-primary dark:text-text-dark-primary text-sm rounded-md"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => removeTag(tag)}
                          className="ml-1 text-text-tertiary dark:text-text-dark-tertiary hover:text-error-500 transition-colors"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {/* Add Tag */}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={currentTag}
                    onChange={(e) => setCurrentTag(e.target.value)}
                    onKeyPress={(e) => handleKeyPress(e, addTag)}
                    placeholder="Add a tag..."
                    className="input flex-1"
                  />
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={addTag}
                    disabled={!currentTag.trim()}
                  >
                    Add
                  </Button>
                </div>

                {/* Tags already used in this workspace. Clicking one reuses its
                    exact spelling, which is what keeps the vocabulary from
                    fragmenting. Narrows as you type. */}
                {tagSuggestions.length > 0 && (
                  <div className="mt-2">
                    <div className="text-xs text-text-tertiary dark:text-text-dark-tertiary mb-1 text-left">
                      {currentTag.trim() ? 'Matching tags' : 'Used in this workspace'}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {tagSuggestions.map((suggestion) => (
                        <button
                          key={suggestion.key}
                          type="button"
                          onClick={() => addSuggestedTag(suggestion.label)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-border-light dark:border-border-dark text-sm text-text-secondary dark:text-text-dark-secondary hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary transition-colors"
                        >
                          {suggestion.label}
                          <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
                            {suggestion.note_count}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
      </form>
    </Modal>
  );
};

export default NoteFormModal;
